import { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { WebhookService, WebhookSubscription, isChatFormat } from '../services/webhook.service';
import { IntegrationBackButton } from '../components/integration-back-button';
import { ChatChannelForm } from '../components/chat-channel-form';
import { WebhookRow } from '../components/webhook-row';

/// Canais do chat da equipe que recebem os avisos; os webhooks de sistema ficam em API e webhooks.
export const TeamChatIntegrations = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    const [list, setList] = useState<WebhookSubscription[] | null>(null);
    const [form, setForm] = useState<WebhookSubscription | 'new' | null>(null);

    const load = useCallback(() => {
        new WebhookService()
            .list()
            .then((res) => setList((res.data.data ?? []).filter((item) => isChatFormat(item.format))))
            .catch(() => setList([]));
    }, []);

    useEffect(load, [load]);

    const remove = (item: WebhookSubscription) =>
        alertDelete(item.id, async () => {
            try {
                await new WebhookService().deleteById(item.id);
                showAlert(t('general.exclusaoSucesso'), 'success', 2500);
                load();
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        }, item.name);

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('webhooks.tituloChat')} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                <IntegrationBackButton />

                {form ? (
                    <ChatChannelForm
                        edit={form === 'new' ? null : form}
                        onClose={() => setForm(null)}
                        onSaved={() => { setForm(null); load(); }}
                    />
                ) : (
                    // minmax(0, 1fr): sem ele a coluna cresce até a URL inteira e o cartão vaza para o lado.
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="caption" sx={{ flex: 1, color: cc.textSecondary }}>
                                {t('webhooks.ajudaChat')}
                            </Typography>

                            {/* Lista vazia já traz o botão no meio; repetir no topo seria ruído. */}
                            {list && list.length > 0 && (
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setForm('new')}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {t('webhooks.adicionarChat')}
                                </MDButton>
                            )}
                        </Box>

                        {!list ? (
                            <Skeleton variant="rounded" height={88} />
                        ) : list.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    justifyItems: 'center',
                                    gap: 1.5,
                                    py: 4,
                                    border: `1px dashed ${cc.borderStrong}`,
                                    borderRadius: '10px',
                                }}
                            >
                                <Typography sx={{ fontSize: 13, color: cc.textMuted }}>
                                    {t('webhooks.semNenhumChat')}
                                </Typography>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setForm('new')}
                                >
                                    {t('webhooks.adicionarChat')}
                                </MDButton>
                            </Box>
                        ) : (
                            list.map((item) => (
                                <WebhookRow
                                    key={item.id}
                                    item={item}
                                    onEdit={() => setForm(item)}
                                    onDelete={() => remove(item)}
                                />
                            ))
                        )}
                    </Box>
                )}
            </Box>
        </DashboardLayout>
    );
};
